import { RefObject, useCallback, useLayoutEffect, useState } from 'react';

type UseDropdownPlacementOptions = {
  containerRef: RefObject<HTMLElement | null>;
  getTopBoundary?: (containerElement: HTMLElement) => number | undefined;
  isOpen: boolean;
  gap?: number;
  maxHeight: number;
  minHeight: number;
  preferredHeight: number;
  viewportGap: number;
};

type FixedPosition = {
  top: number;
  left: number;
  width: number;
};

type DropdownPlacementState = {
  isOpenAbove: boolean;
  maxHeight: number;
  fixedPosition?: FixedPosition;
};

const DROPDOWN_GAP = 8;

const useDropdownPlacement = ({
  containerRef,
  getTopBoundary,
  isOpen,
  gap = DROPDOWN_GAP,
  maxHeight: maxHeightOption,
  minHeight,
  preferredHeight,
  viewportGap,
}: UseDropdownPlacementOptions): DropdownPlacementState => {
  const [state, setState] = useState<DropdownPlacementState>({
    isOpenAbove: false,
    maxHeight: maxHeightOption,
  });

  const updatePlacement = useCallback(() => {
    const containerElement = containerRef.current;

    if (!containerElement) {
      return;
    }

    const containerRect = containerElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const boundaryBottom = viewportHeight - viewportGap;
    const boundaryTop = Math.max(viewportGap, getTopBoundary?.(containerElement) ?? viewportGap);

    const availableBelow = Math.max(0, boundaryBottom - containerRect.bottom - gap);
    const availableAbove = Math.max(0, containerRect.top - boundaryTop - gap);
    const desiredHeight = Math.max(minHeight, preferredHeight);
    const isOpenAbove = availableBelow < desiredHeight && availableAbove > availableBelow;
    const available = isOpenAbove ? availableAbove : availableBelow;
    // The dropdown must fit into the actually usable viewport. A CSS min-height
    // would otherwise push it over persistent controls on short screens.
    const maxHeight = Math.min(maxHeightOption, Math.max(0, available));

    const fixedPosition: FixedPosition = {
      top: isOpenAbove ? containerRect.top - gap - maxHeight : containerRect.bottom + gap,
      left: containerRect.left,
      width: containerRect.width,
    };

    setState((currentState) => {
      const currentPosition = currentState.fixedPosition;
      const hasSamePosition =
        currentPosition?.top === fixedPosition.top &&
        currentPosition?.left === fixedPosition.left &&
        currentPosition?.width === fixedPosition.width;

      return currentState.isOpenAbove === isOpenAbove && currentState.maxHeight === maxHeight && hasSamePosition
        ? currentState
        : { isOpenAbove, maxHeight, fixedPosition };
    });
  }, [containerRef, gap, getTopBoundary, viewportGap, preferredHeight, maxHeightOption, minHeight]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updatePlacement();

    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [isOpen, updatePlacement]);

  return state;
};

export default useDropdownPlacement;
